import type { ResellerPermission, ResellerRole, ResellerRoleKey, TeamMember } from "@xop-any-time/shared";
import { assignableRoles, isBuiltInRole, roleLabel } from "@xop-any-time/shared";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  MenuItem,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent, type JSX } from "react";

import { NoAccessPanel } from "../../components/ui/no-access-panel.js";
import { ApiError, isPermissionDenied } from "../../lib/api.js";
import { PageHeader } from "../portal/crm.js";
import {
  accountKeys,
  createRole,
  deleteCustomRole,
  getRoleMatrix,
  getTeam,
  inviteMember,
  renameRole,
  resetRoleMatrix,
  revokeMember,
  setMemberPermissions,
  setMemberRole,
  setRoleMatrix,
} from "./api.js";
import { PermissionsDrawer } from "./PermissionsDrawer.js";
import { roleErrorMessage } from "./roleErrors.js";
import { RoleMatrixCard } from "./RoleMatrixCard.js";

type RoleOption = { key: ResellerRoleKey; label: string };

/**
 * Your team — the people at YOUR company who can sign in to XOP Reseller.
 *
 * SCOPE IS YOUR OWN COMPANY, NOT YOUR SUBTREE, and that is deliberate rather
 * than an oversight a later change should "fix": a parent reseller can see a
 * child's customers and invoices (that is what the hierarchy is for) but not
 * its staff list. A child company's people are its own business, and a parent
 * that could add users to a child could plant an account inside it.
 *
 * WHAT THIS PAGE'S CONTROLS ARE AND ARE NOT. Everything below keys off
 * `roster.viewer.role`, which comes from crm-api's LIVE membership — not from
 * the session cookie, which is minted at sign-in and stale for hours after a
 * demotion. Even so, hiding a control is a courtesy, never the enforcement:
 * every write here is refused server-side by crm-api from that same live
 * membership. If you are reading this while adding a control, the rule is that
 * the server must already refuse it.
 */
export function TeamPage(): JSX.Element {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: accountKeys.team,
    queryFn: getTeam,
  });
  const [toast, setToast] = useState<string | null>(null);
  const [tab, setTab] = useState<"people" | "roles">("people");
  const [drawerFor, setDrawerFor] = useState<TeamMember | null>(null);
  const [drawerError, setDrawerError] = useState<string | null>(null);
  const [matrixError, setMatrixError] = useState<string | null>(null);

  const isOwnerFromRoster = data?.viewer.role === "owner";

  // An owner needs the matrix everywhere — its custom roles fill the role
  // pickers. Anyone else reads it only on the Roles tab, read-only.
  const matrix = useQuery({
    queryKey: accountKeys.roleMatrix,
    queryFn: getRoleMatrix,
    enabled: isOwnerFromRoster || (data !== undefined && tab === "roles"),
  });

  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: accountKeys.team });
  };

  const role = useMutation({
    mutationFn: ({ id, role: r }: { id: number; role: ResellerRoleKey }) => setMemberRole(id, r),
    onSuccess: () => {
      setToast("Access updated.");
      refresh();
    },
  });

  const revoke = useMutation({
    mutationFn: (id: number) => revokeMember(id),
    onSuccess: () => {
      setToast("Access removed.");
      refresh();
    },
  });

  const perms = useMutation({
    mutationFn: ({
      id,
      ...input
    }: {
      id: number;
      permissionGrants: ResellerPermission[];
      permissionDenies: ResellerPermission[];
    }) => setMemberPermissions(id, input),
    onSuccess: () => {
      setToast("Permissions updated.");
      setDrawerFor(null);
      refresh();
    },
    onError: (e) => setDrawerError(e instanceof ApiError ? e.message : "Could not save."),
  });

  const matrixSave = useMutation({
    mutationFn: ({ role: r, permissions }: { role: string; permissions: ResellerPermission[] }) =>
      setRoleMatrix(r, permissions),
    onSuccess: () => {
      setMatrixError(null);
      void qc.invalidateQueries({ queryKey: accountKeys.roleMatrix });
    },
    onError: (e) => setMatrixError(roleErrorMessage(e)),
  });

  const matrixReset = useMutation({
    mutationFn: (r: string) => resetRoleMatrix(r),
    onSuccess: () => {
      setMatrixError(null);
      void qc.invalidateQueries({ queryKey: accountKeys.roleMatrix });
    },
    onError: (e) => setMatrixError(roleErrorMessage(e)),
  });

  /* Custom roles (2026-09-15). The rename dialog shows its own refusal
     (a taken name belongs next to the name field); a delete's refusal goes
     above the matrix, after the confirmation closes. */
  const roleRename = useMutation({
    mutationFn: ({ role: r, name, permissions }: { role: string; name: string; permissions: ResellerPermission[] }) =>
      renameRole(r, name, permissions),
    onSuccess: () => {
      setMatrixError(null);
      setToast("Role renamed.");
      // Member rows carry the label too.
      void qc.invalidateQueries({ queryKey: accountKeys.roleMatrix });
      refresh();
    },
  });

  const roleDelete = useMutation({
    mutationFn: (r: string) => deleteCustomRole(r),
    onSuccess: () => {
      setMatrixError(null);
      setToast("Role deleted.");
      void qc.invalidateQueries({ queryKey: accountKeys.roleMatrix });
    },
    onError: (e) => setMatrixError(roleErrorMessage(e, "We could not delete the role. Try again.")),
  });

  if (isLoading) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !data) {
    if (isPermissionDenied(error)) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <PageHeader title="Your team" />
          <NoAccessPanel permission={error.permission ?? "team:view"} />
        </Box>
      );
    }
    // A revoked membership answers 403 here. Say so plainly rather than
    // bouncing to /login, which would fail the same check and loop.
    const revoked = error instanceof ApiError && error.status === 403;
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <PageHeader title="Your team" />
        <Alert severity={revoked ? "warning" : "error"}>
          {revoked
            ? "Your access to team management was removed. Contact your XOP Networks channel manager."
            : "We could not load your team just now. Refresh the page to try again."}
        </Alert>
      </Box>
    );
  }

  const isOwner = data.viewer.role === "owner";
  const activeOwners = data.members.filter((m) => m.active && m.role === "owner").length;
  // Owner, the built-ins and the company's custom roles, in matrix order.
  const roleOptions = assignableRoles(matrix.data);
  const nameOf = (m: TeamMember): string => m.roleLabel ?? roleLabel(m.role, matrix.data);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Your team"
        description="People at your company who can sign in to XOP Reseller."
      />

      {/* Everyone sees both tabs; only an owner gets controls on either. */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab value="people" label="People" />
        <Tab value="roles" label="Roles" />
      </Tabs>

      {!isOwner && (
        <Alert severity="info">
          Only an owner at your company can add people, change roles or permissions. You can see
          who has access and what each role may do.
        </Alert>
      )}

      {tab === "roles" ? (
        <>
          {isOwner && <AddRoleCard onCreated={(label) => setToast(`Role "${label}" created. Tick what it may do below.`)} />}
          {matrixError && (
            <Alert severity="error" onClose={() => setMatrixError(null)}>
              {matrixError}
            </Alert>
          )}
          {matrix.isError ? (
            // The matrix is gated on membership, not on a catalog key, so a
            // refusal names its key only when crm-api sends one; without it
            // the generic error below is the honest answer.
            isPermissionDenied(matrix.error) && matrix.error.permission ? (
              <NoAccessPanel permission={matrix.error.permission} />
            ) : (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={() => matrix.refetch()}>
                    Retry
                  </Button>
                }
              >
                We could not load the role matrix. Refresh the page to try again.
              </Alert>
            )
          ) : matrix.data ? (
            <RoleMatrixCard
              matrix={matrix.data}
              readOnly={!isOwner}
              saving={matrixSave.isPending || matrixReset.isPending || roleRename.isPending || roleDelete.isPending}
              onChange={(r, permissions) => matrixSave.mutate({ role: r, permissions })}
              onReset={(r) => matrixReset.mutate(r)}
              onRename={(r, name, permissions) => roleRename.mutateAsync({ role: r, name, permissions })}
              // Never rejects: the mutation's onError reports the refusal.
              onDelete={(r) => roleDelete.mutateAsync(r).catch(() => undefined)}
            />
          ) : (
            <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          )}
        </>
      ) : (
        <>
          {isOwner && <InviteCard options={roleOptions} onDone={(m) => setToast(m)} />}

          <Card>
            <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Email</TableCell>
                      <TableCell>Access</TableCell>
                      <TableCell>Status</TableCell>
                      {isOwner && <TableCell align="right">Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.members.map((m) => (
                      <MemberRow
                        key={m.id}
                        member={m}
                        roleName={nameOf(m)}
                        options={roleOptions}
                        isOwner={isOwner}
                        /* The server refuses demoting or revoking the last active
                           owner — a company that locks itself out needs XOP to
                           unlock it. Reflected here so the control is not offered
                           and then refused. */
                        lastOwner={m.active && m.role === "owner" && activeOwners <= 1}
                        busy={role.isPending || revoke.isPending}
                        onRole={(r) => role.mutate({ id: m.id, role: r })}
                        onRevoke={() => revoke.mutate(m.id)}
                        onPermissions={() => {
                          setDrawerFor(m);
                          setDrawerError(null);
                        }}
                      />
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </CardContent>
          </Card>

          {(role.isError || revoke.isError) && (
            <Alert severity="error">
              {(role.error ?? revoke.error) instanceof ApiError
                ? ((role.error ?? revoke.error) as ApiError).message
                : "That did not work."}
            </Alert>
          )}
        </>
      )}

      <PermissionsDrawer
        member={drawerFor}
        matrix={matrix.data}
        open={drawerFor !== null}
        saving={perms.isPending}
        error={drawerError}
        onClose={() => {
          setDrawerFor(null);
          setDrawerError(null);
        }}
        onSave={(next) => drawerFor && perms.mutate({ id: drawerFor.id, ...next })}
      />

      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        message={toast ?? ""}
      />
    </Box>
  );
}

function MemberRow({
  member,
  roleName,
  options,
  isOwner,
  lastOwner,
  busy,
  onRole,
  onRevoke,
  onPermissions,
}: {
  member: TeamMember;
  roleName: string;
  options: RoleOption[];
  isOwner: boolean;
  lastOwner: boolean;
  busy: boolean;
  onRole: (role: ResellerRoleKey) => void;
  onRevoke: () => void;
  onPermissions?: () => void;
}): JSX.Element {
  // A member's current role is always an option, even before the matrix (and
  // its custom roles) has loaded — MUI renders a value with no option as blank.
  const choices = options.some((o) => o.key === member.role)
    ? options
    : [...options, { key: member.role, label: roleName }];
  return (
    <TableRow
      data-testid={`member-row-${member.email}`}
      /* A revoked person stays on the list, greyed out. "Never invited" and
         "access removed" are different facts and an owner needs to tell them
         apart — the same choice the command center's roster makes. */
      sx={{ opacity: member.active ? 1 : 0.55 }}
    >
      <TableCell>
        {member.email}
        {member.self && (
          <Chip label="you" size="small" variant="outlined" sx={{ ml: 1, height: 18 }} />
        )}
      </TableCell>
      <TableCell>{roleName}</TableCell>
      <TableCell>
        <Chip
          size="small"
          label={member.active ? "Active" : "Removed"}
          color={member.active ? "success" : "default"}
          variant="outlined"
        />
      </TableCell>
      {isOwner && (
        <TableCell align="right">
          {member.active && !member.self && (
            <Box sx={{ display: "inline-flex", gap: 1 }}>
              <TextField
                select
                size="small"
                value={member.role}
                disabled={busy || lastOwner}
                onChange={(e) => onRole(e.target.value as ResellerRoleKey)}
                sx={{ minWidth: 140 }}
                inputProps={{ "aria-label": "Role" }}
              >
                {choices.map((r) => (
                  <MenuItem key={r.key} value={r.key}>
                    {r.label}
                  </MenuItem>
                ))}
              </TextField>
              {member.role !== "owner" && (
                <Button size="small" onClick={onPermissions}>
                  Permissions
                </Button>
              )}
              <Button
                size="small"
                color="error"
                disabled={busy || lastOwner}
                onClick={onRevoke}
              >
                Remove
              </Button>
            </Box>
          )}
          {member.self && (
            <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
              Ask another owner
            </Typography>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}

/** What each role can do, shown as helper text under the invite form's role
 * picker. One line per role — including technician and viewer, which used
 * to fall through to the salesperson copy because the old helper text was a
 * binary owner/everyone-else branch. */
const ROLE_HINTS: Record<ResellerRole, string> = {
  owner: "Manages the team, roles and everything else.",
  salesperson: "Sells: customers, contacts, invoices and quotes; sees licences and documentation.",
  technician: "Issues licences; sees customers, contacts and documentation.",
  viewer: "Read-only across the portal.",
};

/** A custom role's permissions are whatever the owner ticked — there is no
 * fixed sentence for it. */
const CUSTOM_ROLE_HINT = "Your company's own role — what it may do is set on the Roles tab.";

const hintFor = (role: ResellerRoleKey): string => (isBuiltInRole(role) ? ROLE_HINTS[role] : CUSTOM_ROLE_HINT);

function InviteCard({
  options,
  onDone,
}: {
  options: RoleOption[];
  onDone: (message: string) => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<ResellerRoleKey>("salesperson");

  const invite = useMutation({
    mutationFn: () => inviteMember({ email, role }),
    onSuccess: (res) => {
      setEmail("");
      // `invited: false` means the ACCESS was granted but the email did not
      // go out. Both halves are said, because "we sent an invitation" would
      // be false and "it failed" would be wrong too.
      onDone(
        res.invited
          ? `Invitation sent to ${res.member.email}.`
          : `${res.member.email} has access, but the invitation email could not be sent. Ask your XOP Networks channel manager to resend it.`,
      );
      void qc.invalidateQueries({ queryKey: accountKeys.team });
    },
  });

  return (
    <Card>
      <CardContent
        component="form"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (email.trim() !== "") invite.mutate();
        }}
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        <Typography sx={{ fontWeight: 600 }}>Add someone</Typography>
        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
          They will get an email inviting them to set a password. Until they do, they cannot
          sign in.
        </Typography>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ flex: "1 1 260px" }}
            inputProps={{ maxLength: 255 }}
          />
          <TextField
            select
            label="Access"
            value={role}
            onChange={(e) => setRole(e.target.value as ResellerRoleKey)}
            sx={{ minWidth: 180 }}
            helperText={hintFor(role)}
          >
            {options.map((r) => (
              <MenuItem key={r.key} value={r.key}>
                {r.label}
              </MenuItem>
            ))}
          </TextField>
          <Button
            type="submit"
            variant="contained"
            disabled={invite.isPending || email.trim() === ""}
            sx={{ mt: 1 }}
          >
            {invite.isPending ? "Sending…" : "Send invitation"}
          </Button>
        </Box>

        {invite.isError && (
          <Alert severity="error">
            {invite.error instanceof ApiError
              ? invite.error.message
              : "We could not add that person."}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * "Add role" (custom roles, 2026-09-15). A new role starts with NO permissions
 * — the owner ticks them in the matrix below, where the closure rule is on
 * screen — rather than silently copying a built-in whose contents nobody chose.
 */
function AddRoleCard({ onCreated }: { onCreated: (label: string) => void }): JSX.Element {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const trimmed = name.trim();

  const create = useMutation({
    mutationFn: (n: string) => createRole(n),
    onSuccess: (_matrix, n) => {
      setName("");
      onCreated(n);
      void qc.invalidateQueries({ queryKey: accountKeys.roleMatrix });
    },
  });

  return (
    <Card>
      <CardContent
        component="form"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (trimmed !== "") create.mutate(trimmed);
        }}
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        <Typography sx={{ fontWeight: 600 }}>Add a role</Typography>
        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
          Create a role of your own beside Salesperson, Technician and Viewer. It starts with no
          permissions; tick what it may do in the table below, then give it to people on the People tab.
        </Typography>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
          <TextField
            label="Role name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ flex: "1 1 260px" }}
            inputProps={{ maxLength: 60 }}
          />
          <Button type="submit" variant="contained" disabled={create.isPending || trimmed === ""} sx={{ mt: 1 }}>
            {create.isPending ? "Creating…" : "Create role"}
          </Button>
        </Box>
        {create.isError && (
          <Alert severity="error">{roleErrorMessage(create.error, "We could not create the role. Try again.")}</Alert>
        )}
      </CardContent>
    </Card>
  );
}
